import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const validText = (value, max) => typeof value === 'string' && Boolean(value.trim()) &&
  value.length <= max && !/[\u0000-\u001f\u007f-\u009f]/.test(value) &&
  !/[\ud800-\udfff]/u.test(value);
const validId = value => validText(value, 512) && value !== '.' && value !== '..' && !/[\s/\\?#]/.test(value);
const ORIGIN_FIELDS = new Set(['place_id', 'formatted_address', 'latitude', 'longitude']);
const exactOriginShape = value => value !== null && typeof value === 'object' && !Array.isArray(value) &&
  Object.keys(value).length === ORIGIN_FIELDS.size &&
  Object.keys(value).every(field => ORIGIN_FIELDS.has(field));
export const validOrigin = value => exactOriginShape(value) &&
  validId(value.place_id) && validText(value.formatted_address, 2000) &&
  Number.isFinite(value.latitude) && Math.abs(value.latitude) <= 90 &&
  Number.isFinite(value.longitude) && Math.abs(value.longitude) <= 180;
const messages = {
  INVALID_PLACES_REQUEST: 'Please enter a valid address search.',
  INVALID_DELIVERY_DESTINATION: 'Select a valid address from the suggestions.',
  DELIVERY_DESTINATION_NOT_FOUND: 'This address is unavailable. Search for another address.',
  DELIVERY_ROUTE_UNAVAILABLE: 'Address lookup is unavailable. Please try again.',
  DELIVERY_PROVIDER_TIMEOUT: 'Address lookup timed out. Please try again.',
  DELIVERY_PROVIDER_RATE_LIMITED: 'Address lookup is busy. Please try again later.',
  DELIVERY_PROVIDER_REJECTED: 'Address lookup could not be completed.',
  INVALID_PLACE_SUGGESTIONS: 'Address suggestions could not be verified. Try again.',
  INVALID_PLACE_DETAILS: 'The address could not be verified. Search again.',
  INVALID_PROVIDER_RESPONSE: 'Address lookup returned an invalid result. Try again.',
  INVALID_DELIVERY_SETTINGS: 'Stored delivery settings need attention before saving an origin.',
};
function safeError(response) {
  if (response?.status === 429) return 'Too many requests. Please wait before trying again.';
  if (response?.status === 409) return 'Restaurant settings changed. Review the current origin before retrying.';
  if ([401, 403].includes(response?.status)) return 'You are not authorized to change this restaurant origin.';
  return messages[response?.data?.code] || 'Unable to complete the address request. Please try again.';
}
function newSession() {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

export default function RestaurantDeliveryOrigin({ restaurantId, location, canEdit, disabled, needsReconciliation = false,
  cooldownUntil, onCooldown,
  beginMutation, endMutation, onLocation }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [attempt, setAttempt] = useState(0);
  const [cooling, setCooling] = useState(() => Date.now() < cooldownUntil);
  const scope = useRef(null);
  const session = useRef(null);
  const revision = useRef(0);
  const controller = useRef(null);
  const blocked = useRef(false);
  const pending = useRef(false);
  const uncertain = useRef(false);
  const latest = useRef({ needsReconciliation, onCooldown, endMutation, onLocation });

  // Refresh committed callbacks without restarting restaurant or request lifecycles.
  useEffect(() => {
    latest.current = { needsReconciliation, onCooldown, endMutation, onLocation };
  });

  useEffect(() => {
    const current = { id: restaurantId };
    // Reconciliation is an entry condition for this scope. Same-restaurant prop
    // updates must not reset drafts or bypass the explicit reconciliation flow.
    const reconcileOnEntry = latest.current.needsReconciliation;
    scope.current = current;
    session.current = null; blocked.current = false; pending.current = false; uncertain.current = reconcileOnEntry;
    setQuery(''); setSuggestions([]); setError(''); setStatus('idle'); setActiveIndex(-1);
    if (reconcileOnEntry) {
      setStatus('uncertain'); setError('Reload the current origin before making further changes.');
    }
    return () => {
      scope.current = null; revision.current += 1; controller.current?.abort();
      if (pending.current) latest.current.endMutation(true);
    };
  }, [restaurantId]);

  useEffect(() => {
    const remaining = cooldownUntil - Date.now();
    setCooling(remaining > 0);
    if (remaining <= 0) return;
    const timer = setTimeout(() => setCooling(false), Math.min(remaining, 2147483647));
    return () => clearTimeout(timer);
  }, [cooldownUntil, cooling]);

  const applyCooldown = useCallback(response => {
    if (response?.status !== 429) return;
    const raw = response.headers?.get?.('Retry-After') ?? response.headers?.['retry-after'];
    const seconds = typeof raw === 'string' && /^[0-9]+$/.test(raw) ? Number(raw) : NaN;
    const valid = Number.isSafeInteger(seconds) && seconds > 0 && seconds <= (8640000000000000 - Date.now()) / 1000;
    latest.current.onCooldown(Date.now() + (valid ? seconds : 60) * 1000, restaurantId);
    blocked.current = true;
  }, [restaurantId]);

  useEffect(() => {
    if (!restaurantId || !canEdit || disabled || blocked.current || pending.current || uncertain.current ||
        Date.now() < cooldownUntil || query.trim().length < 3 || !validText(query, 200)) return;
    const current = scope.current;
    const generation = ++revision.current;
    const abort = new AbortController(); controller.current = abort;
    const timer = setTimeout(async () => {
      setStatus('searching'); setError('');
      try {
        session.current ||= newSession();
        const { data } = await axios.post(`${API_BASE_URL}/restaurants/${restaurantId}/delivery-origin/autocomplete`,
          { query: query.trim(), session_token: session.current }, { withCredentials: true, signal: abort.signal });
        if (scope.current !== current || revision.current !== generation || abort.signal.aborted) return;
        if (!Array.isArray(data?.suggestions) || data.suggestions.length > 5 ||
            !data.suggestions.every(item => validId(item?.place_id) && validText(item?.text, 2000))) {
          throw new Error('invalid suggestions');
        }
        setSuggestions(data.suggestions); setActiveIndex(-1); setStatus(data.suggestions.length ? 'results' : 'empty');
      } catch (failure) {
        if (scope.current !== current) return;
        applyCooldown(failure.response);
        if (revision.current !== generation || abort.signal.aborted) return;
        blocked.current = true;
        setSuggestions([]); setStatus('error'); setError(safeError(failure.response));
      }
    }, 300);
    return () => { clearTimeout(timer); abort.abort(); };
  }, [query, attempt, restaurantId, canEdit, disabled, cooldownUntil, applyCooldown]);

  const reconcile = async (current, abort) => {
    const { data } = await axios.get(`${API_BASE_URL}/restaurants/${current.id}`,
      { withCredentials: true, signal: abort.signal });
    if (scope.current !== current || abort.signal.aborted) return;
    if (!data || typeof data !== 'object' || Array.isArray(data) ||
        (data.delivery_settings != null && (typeof data.delivery_settings !== 'object' || Array.isArray(data.delivery_settings)))) {
      throw new Error('invalid restaurant');
    }
    const saved = data.delivery_settings?.delivery_location ?? null;
    if (saved !== null && !validOrigin(saved)) throw new Error('invalid origin');
    latest.current.onLocation(saved, current.id);
    uncertain.current = false;
    setStatus('error'); setError('Current origin reloaded. Review it before trying again.');
  };

  const select = async suggestion => {
    if (!canEdit || disabled || pending.current || uncertain.current || Date.now() < cooldownUntil || !session.current) return;
    if (!beginMutation()) return;
    const current = scope.current;
    pending.current = true; blocked.current = true;
    revision.current += 1; controller.current?.abort();
    const abort = new AbortController(); controller.current = abort;
    const token = session.current; session.current = null;
    setSuggestions([]); setActiveIndex(-1); setStatus('finalizing'); setError('');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/restaurants/${current.id}/delivery-origin/finalize`,
        { delivery_place_id: suggestion.place_id, session_token: token }, { withCredentials: true, signal: abort.signal });
      if (scope.current !== current || abort.signal.aborted) return;
      if (!validOrigin(data?.delivery_location) || data.delivery_location.place_id !== suggestion.place_id) {
        throw new Error('invalid origin');
      }
      latest.current.onLocation(data.delivery_location, current.id);
      setQuery(''); setStatus('saved');
    } catch (failure) {
      if (scope.current !== current) return;
      applyCooldown(failure.response);
      setError(safeError(failure.response)); setStatus('error');
      // Lost acknowledgements and malformed successes may follow a committed write.
      if (!failure.response || failure.response.status >= 500 || failure.response.status === 409) {
        uncertain.current = true;
        try { await reconcile(current, abort); }
        catch {
          if (scope.current === current) {
            setStatus('uncertain'); setError('Unable to confirm the saved origin. Reload before making further changes.');
          }
        }
      }
    } finally {
      if (scope.current === current) { pending.current = false; latest.current.endMutation(uncertain.current); }
    }
  };

  const reload = async () => {
    if (pending.current || !beginMutation(true)) return;
    const current = scope.current;
    const abort = new AbortController(); controller.current = abort;
    pending.current = true; setStatus('finalizing');
    try { await reconcile(current, abort); }
    catch { if (scope.current === current) { setStatus('uncertain'); setError('Unable to confirm the saved origin. Please reload again.'); } }
    finally { if (scope.current === current) { pending.current = false; latest.current.endMutation(uncertain.current); } }
  };
  const edit = value => {
    if (pending.current || uncertain.current) return;
    revision.current += 1; controller.current?.abort();
    blocked.current = Date.now() < cooldownUntil;
    setQuery(value.slice(0, 200)); setSuggestions([]); setActiveIndex(-1); setError(''); setStatus('idle');
  };
  const busy = status === 'finalizing';
  return <section aria-label="Restaurant Delivery Origin" className="space-y-3" aria-busy={busy || status === 'searching'}>
    <h3 className="font-semibold">Restaurant Delivery Origin</h3>
    <p data-testid="saved-delivery-origin" className="text-sm">
      {validOrigin(location) ? location.formatted_address : 'No delivery origin configured.'}
    </p>
    {canEdit && <>
      <p className="text-sm text-muted-foreground">Selecting an address saves the origin immediately. Other delivery settings use Save.</p>
      <label htmlFor="restaurant-origin-search" className="block text-sm font-medium">Search for a restaurant address</label>
      <Input id="restaurant-origin-search" role="combobox" autoComplete="off" maxLength={200} value={query}
        disabled={disabled || busy || uncertain.current || cooling} aria-autocomplete="list"
        aria-expanded={suggestions.length > 0} aria-controls="restaurant-origin-options"
        aria-activedescendant={activeIndex >= 0 ? `restaurant-origin-option-${activeIndex}` : undefined}
        aria-describedby="restaurant-origin-status" onChange={event => edit(event.target.value)}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' && suggestions.length) { event.preventDefault(); setActiveIndex(i => (i + 1) % suggestions.length); }
          if (event.key === 'ArrowUp' && suggestions.length) { event.preventDefault(); setActiveIndex(i => i <= 0 ? suggestions.length - 1 : i - 1); }
          if (event.key === 'Escape') { revision.current += 1; controller.current?.abort(); blocked.current = true; setSuggestions([]); setActiveIndex(-1); }
          if (event.key === 'Enter') { event.preventDefault(); if (suggestions[activeIndex]) select(suggestions[activeIndex]); }
        }} />
      <ul id="restaurant-origin-options" role="listbox" aria-label="Restaurant address suggestions">
        {suggestions.map((item, index) => <li key={`${item.place_id}:${index}`} role="option"
          id={`restaurant-origin-option-${index}`} aria-selected={index === activeIndex}>
          <Button type="button" variant="ghost" className="h-auto min-h-[44px] w-full justify-start whitespace-normal text-left"
            disabled={disabled || busy || cooling || uncertain.current} onClick={() => select(item)}>{item.text}</Button>
        </li>)}
      </ul>
      <p id="restaurant-origin-status" role="status" className="text-sm text-muted-foreground">
        {cooling ? 'Please wait before another address request.' : busy ? 'Saving and verifying origin...' :
          status === 'searching' ? 'Searching addresses...' : status === 'saved' ? 'Delivery origin saved.' :
          status === 'empty' ? 'No addresses found. Try another search.' : 'Type at least 3 characters and select an address.'}
      </p>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {status === 'uncertain' ? <Button type="button" variant="outline" disabled={disabled || busy} onClick={reload}>Reload origin</Button> :
        status === 'error' && <Button type="button" variant="outline" disabled={disabled || cooling || busy} onClick={() => {
          blocked.current = false; setError(''); setStatus('idle'); setAttempt(value => value + 1);
        }}>Retry address search</Button>}
    </>}
  </section>;
}
